import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Save, X, ImageIcon } from "lucide-react";
import { api, formatApiError, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import StorefrontAssetSlot from "@/components/admin/StorefrontAssetSlot";

const CATEGORY_IMAGE_SPEC = {
    accept: "image/jpeg,image/png,image/webp",
    acceptLabel: "JPG / PNG / WebP",
    exactWidth: 900,
    exactHeight: 900,
    preferredKb: 200,
    recommendation: "900 × 900 px square JPG, under 200 KB. Falls back to the global category default if empty.",
};

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | id
    const [form, setForm] = useState({ name: "", slug: "", description: "", image_url: "", sort_order: 0, active: true });
    const [error, setError] = useState("");

    const load = async () => {
        const { data } = await api.get("/admin/categories");
        setItems(data || []);
    };
    useEffect(() => { load(); }, []);

    const startEdit = (it) => {
        setEditing(it.id);
        setForm({ name: it.name, slug: it.slug, description: it.description || "", image_url: it.image_url || "", sort_order: it.sort_order || 0, active: it.active !== false });
    };
    const startNew = () => {
        setEditing("new");
        setForm({ name: "", slug: "", description: "", image_url: "", sort_order: 0, active: true });
    };
    const cancel = () => { setEditing(null); setError(""); };

    const save = async () => {
        setError("");
        try {
            if (editing === "new") {
                await api.post("/admin/categories", form);
                toast.success("Category created");
            } else {
                await api.put(`/admin/categories/${editing}`, form);
                toast.success("Category updated");
            }
            setEditing(null);
            await load();
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        }
    };
    const remove = async (it) => {
        if (!window.confirm(`Delete ${it.name}?`)) return;
        try {
            await api.delete(`/admin/categories/${it.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-categories-page">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Catalog</div>
                    <h1 className="font-display text-3xl font-black uppercase tracking-tight">Categories</h1>
                </div>
                <button onClick={startNew} className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#33C9EA]" data-testid="cat-new">
                    <Plus size={16} /> New
                </button>
            </div>

            {(editing !== null) && (
                <div className="bg-[#18181B] border border-[#27272A] rounded-sm p-4 space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                        <FieldInput label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cat-name" />
                        <FieldInput label="Slug (auto)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="cat-slug" />
                        <FieldInput label="Sort order" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) })} testid="cat-sort" />
                    </div>

                    <div>
                        <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1.5">Category image</label>
                        <StorefrontAssetSlot
                            label={form.name || "Category image"}
                            value={form.image_url || ""}
                            spec={CATEGORY_IMAGE_SPEC}
                            testId="cat-image-slot"
                            onChange={(url) => setForm({ ...form, image_url: url })}
                        />
                        <div className="mt-2">
                            <label className="block text-[10px] uppercase tracking-widest font-display font-bold text-white/45 mb-1">Or paste an external image URL</label>
                            <input
                                type="text"
                                value={form.image_url || ""}
                                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                placeholder="https://…"
                                className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2 text-white text-sm focus:border-[#00BCE5] outline-none"
                                data-testid="cat-image-url"
                            />
                        </div>
                    </div>

                    <FieldInput label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="cat-desc" />
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#00BCE5]" /> Active
                    </label>
                    {error && <div className="text-sm text-[#FF3B30]">{error}</div>}
                    <div className="flex gap-2">
                        <button onClick={save} className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-4 py-2 rounded-sm" data-testid="cat-save">
                            <Save size={14} /> Save
                        </button>
                        <button onClick={cancel} className="inline-flex items-center gap-2 border border-[#27272A] text-white px-4 py-2 rounded-sm font-display uppercase tracking-widest font-bold">
                            <X size={14} /> Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-[#18181B] border border-[#27272A] rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#121214] text-xs uppercase tracking-widest font-display text-white/55">
                        <tr>
                            <th className="px-3 py-2 text-left w-14">Image</th>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Slug</th>
                            <th className="px-3 py-2 text-left">Sort</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((c) => (
                            <tr key={c.id} className="border-t border-[#27272A]" data-testid={`cat-row-${c.slug}`}>
                                <td className="px-3 py-2">
                                    <CategoryThumb url={c.image_url} alt={c.name} />
                                </td>
                                <td className="px-3 py-2 font-medium text-white">{c.name}</td>
                                <td className="px-3 py-2 text-white/55">{c.slug}</td>
                                <td className="px-3 py-2 text-white/85">{c.sort_order || 0}</td>
                                <td className="px-3 py-2 text-xs uppercase font-display tracking-widest">
                                    {c.active ? <span className="text-[#34C759]">Active</span> : <span className="text-white/45">Inactive</span>}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="inline-flex gap-2">
                                        <button onClick={() => startEdit(c)} className="p-1.5 border border-[#27272A] rounded-sm text-white/85 hover:border-[#00BCE5] hover:text-[#00BCE5]" data-testid={`cat-edit-${c.slug}`}>
                                            <Edit2 size={14} />
                                        </button>
                                        <button onClick={() => remove(c)} className="p-1.5 border border-[#27272A] rounded-sm text-white/85 hover:border-[#FF3B30] hover:text-[#FF3B30]" data-testid={`cat-delete-${c.slug}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-white/55">No categories yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function FieldInput({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2 text-white focus:border-[#00BCE5] outline-none" data-testid={testid} />
        </div>
    );
}

function CategoryThumb({ url, alt }) {
    const src = url ? imageUrl(url) : "";
    return (
        <div
            className="w-10 h-10 rounded-sm border border-[#27272A] bg-[#0A0A0B] overflow-hidden flex items-center justify-center"
            data-testid="cat-row-thumb"
        >
            {src ? (
                <img src={src} alt={alt || "Category"} className="w-full h-full object-cover" />
            ) : (
                <ImageIcon size={14} className="text-white/30" />
            )}
        </div>
    );
}
